import { useEffect, useMemo, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import debounce from 'lodash/debounce';

import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import { TEMPORARY_ID_PREFIX, useUpdateDefect } from '@/models/article';
import { ArticleDefectWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { createBEMClasses } from '@/utils/classname';

import { getEditedFields } from './getEditedFields';

const { element } = createBEMClasses('defect-card');

export const DefectContextMobile = ({
  defect,
  previousDefect,
}: {
  defect?: ArticleDefectWithRelations;
  previousDefect?: ArticleDefectWithRelations;
}) => {
  const defectOrPreviousDefect = defect ?? previousDefect!;

  if (!defectOrPreviousDefect.context) {
    return null;
  }

  return (
    <Stack
      gap="0.25rem"
      className={element('context', {
        edited: getEditedFields({ defect, previousDefect }).isContextEdited,
      })}
    >
      <Paragraph>&ldquo;{defectOrPreviousDefect.context}&rdquo;</Paragraph>
    </Stack>
  );
};

export const DefectContextInput = ({
  defect,
  previousDefect,
}: {
  defect?: ArticleDefectWithRelations;
  previousDefect?: ArticleDefectWithRelations;
}) => {
  const { request, article, view, errors } = useArticleContext();

  const isEditable =
    !!defect && view.defects.fields.context.editable && !defect.id.startsWith(TEMPORARY_ID_PREFIX);
  const isRequired = view.defects.fields.context.required;

  const { mutateAsync: updateDefect } = useUpdateDefect({
    requestId: request.id,
    articleId: article.id,
  });

  const debouncedUpdateDefect = useMemo(() => {
    return debounce(updateDefect, 500);
  }, [updateDefect]);

  const defectOrPreviousDefect = defect ?? previousDefect;
  const [context, setContext] = useState(defectOrPreviousDefect?.context ?? '');

  useEffect(() => {
    setContext(defectOrPreviousDefect?.context ?? '');
    debouncedUpdateDefect.cancel();
  }, [defectOrPreviousDefect?.context, debouncedUpdateDefect]);

  if (!defectOrPreviousDefect?.context && !isEditable) {
    return null;
  }

  return (
    <TextArea
      label={
        <>
          <InlineText size="100" color="secondary">
            <Trans id="defect-card.context.label">Context</Trans>
          </InlineText>
          {isEditable && !isRequired && <OptionalLabel />}
        </>
      }
      isInvalid={errors.defects?.defects?.find(({ id }) => id === defect?.id)?.missingContext}
      ariaLabel={t({
        id: 'defect-card.context.aria-label',
        message: 'Context of the defect',
      })}
      placeholder={t({
        id: 'defect-card.context.placeholder',
        message: 'Describe how the defect appeared...',
      })}
      value={context}
      onChange={(event) => {
        if (defect) {
          const currentContext = defect.context;

          setContext(event.target.value);
          debouncedUpdateDefect({
            defectId: defect.id,
            body: {
              context: event.target.value.trim(),
            },
          })?.catch(() => setContext(currentContext ?? ''));
        }
      }}
      disabled={!isEditable}
      className={element('context', {
        edited: getEditedFields({ defect, previousDefect }).isContextEdited,
      })}
    />
  );
};
